import { Card } from "@/components/_ui/card";
import { deriveTrackingLifecycle } from "@/utils/publicTracking/lifecycle";
import type { PublicTrackingStatus } from "@/utils/publicTracking/types";
import type { ScanJourney } from "@/utils/publicTracking/useScanJourneyMap";

import { ExpectedDeliveryCard } from "./ExpectedDeliveryCard";
import { LifecycleStepper } from "./LifecycleStepper";
import { LiveRouteSection } from "./LiveRouteSection";
import { ScanTimeline } from "./ScanTimeline";
import { StatusHero } from "./StatusHero";

interface PublicTrackingDashboardProps {
  data: PublicTrackingStatus;
  /** Already-resolved journey from the form's `useScanJourneyMap`. */
  journey: ScanJourney;
}

/**
 * Two-column tracking dashboard: status hero + delivery card, the lifecycle
 * stepper, then a combined Scan-History / Live-Route detail card. The
 * backend owns the overall `status`; we only derive per-step dates + map.
 */
export function PublicTrackingDashboard({
  data,
  journey,
}: PublicTrackingDashboardProps) {
  const lifecycle = deriveTrackingLifecycle(data);
  const delivered = data.status === "delivered";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <StatusHero status={data.status} imb={data.imb} />
        <ExpectedDeliveryCard
          expectedDate={data.expected_delivery_date}
          deliveredDate={data.delivered_date}
        />
      </div>

      <LifecycleStepper lifecycle={lifecycle} />

      <Card className="divide-border grid grid-cols-1 divide-y overflow-hidden py-0 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
        <div className="p-6">
          <ScanTimeline events={data.events} />
        </div>
        <div className="p-6">
          <LiveRouteSection
            data={data}
            journey={journey}
            delivered={delivered}
          />
        </div>
      </Card>
    </div>
  );
}
